/**
 * Font-name resolution for Figma export.
 *
 * Customers upload brand fonts as individual files; each file is registered
 * as its own CSS family using the filename stem (e.g. `AvenirNextLTPro-Bold`).
 * In CSS that family has a single face, so changing the layer's `fontWeight`
 * or `italic` doesn't switch files — the browser just applies faux bold /
 * faux italic on top of the file's own cut. The editor inherits this
 * behavior, so what the customer actually sees is the file as-is with any
 * additional weight/italic synthesized on top.
 *
 * Figma, by contrast, models fonts as `(family, style)` pairs against the
 * fonts it has registered. The resolver looks the layer's `fontFamily` up in
 * the ad's brand-font catalog and derives the pair from the font file's own
 * stored metadata (`deriveFontFamily` / `deriveFigmaStyle`). Face choice
 * prefers a matching italic slope, then the nearest weight. A single-weight
 * file wins a remaining tie with a variable range. Fonts uploaded before the
 * metadata fields existed (or that fontkit could not read) fall back to
 * parsing the filename — the pre-metadata resolver kept verbatim below.
 */
import {
  cssWeightToNumber,
  deriveFigmaStyle,
  deriveFontFamily,
  isVariableFontFile,
  namedVariationWeights,
  type NamedWeightInstance,
  styleWithItalic,
  weightToStyleName,
} from "../../brand-profiles/assets/font-identity";
import type { BrandFontFile } from "../../brand-profiles/types";

export interface FigmaFontCandidate {
  family: string;
  style: string;
}

export interface FigmaFontResolution {
  family: string;
  style: string;
  /**
   * Ordered fallback candidates the plugin should try (in order) when the
   * primary `(family, style)` isn't registered in Figma. Excludes the
   * primary. Plugin should attempt `figma.loadFontAsync` (or check
   * `listAvailableFontsAsync`) for each until one succeeds.
   *
   * Covers two real divergences between our naming and what's actually in
   * Figma:
   *   - Compound family form: `Arial + Black` may be registered as
   *     `"Arial Black" + "Regular"` rather than a Black weight of Arial.
   *   - Style-name spacing: `Montserrat + "Extra Bold"` may be registered as
   *     `"ExtraBold"` (no space) on some fonts.
   */
  alternatives: FigmaFontCandidate[];
}

interface IndexedFontFace {
  derivedFamily: string;
  file: BrandFontFile;
  weightInstances: readonly NamedWeightInstance[];
}

export type FigmaFontIndex = Map<string, IndexedFontFace[]>;

export function buildFigmaFontIndex(
  files: readonly BrandFontFile[],
): FigmaFontIndex {
  const index: FigmaFontIndex = new Map();
  const add = (name: string, face: IndexedFontFace) => {
    const key = name.trim().toLowerCase();
    if (!key) return;
    const faces = index.get(key);
    if (!faces) {
      index.set(key, [face]);
    } else if (!faces.includes(face)) {
      faces.push(face);
    }
  };

  for (const file of files) {
    const derivedFamily = deriveFontFamily(file);
    if (!derivedFamily) continue;
    const face: IndexedFontFace = {
      derivedFamily,
      file,
      weightInstances: namedVariationWeights(file.namedVariations),
    };
    const stem = file.fileName?.replace(/\.[^.]+$/, "");
    if (stem) add(stem, face);
    add(derivedFamily, face);
    // `familyName` is the CSS address markup is written against. After a
    // collision rename ("Knockout 69") it differs from the typographic
    // family, and the layer must still land on this face's real identity.
    if (file.familyName) add(file.familyName, face);
  }
  return index;
}

export function resolveFigmaFontForLayer(args: {
  index: FigmaFontIndex;
  fontFamily: string;
  fontWeight: string;
  italic: boolean;
}): FigmaFontResolution {
  const { index, fontFamily, fontWeight, italic } = args;
  const normalized = fontFamily.replace(/^['"]|['"]$/g, "").trim();
  const legacy = resolveFromFilename(normalized || fontFamily);

  const faces = normalized ? index.get(normalized.toLowerCase()) : undefined;
  if (faces && faces.length > 0) {
    const face = selectPreferredFace(faces, {
      targetWeight: cssWeightToNumber(fontWeight),
      italic,
    });
    const family = face.derivedFamily;
    const style = deriveFigmaStyle({
      file: face.file,
      weightInstances: face.weightInstances,
      cssWeight: fontWeight,
      italic,
    });
    return withAlternatives({ family, style }, legacy);
  }

  const family = normalized || fontFamily;
  const style = styleWithItalic(
    weightToStyleName(cssWeightToNumber(fontWeight)),
    italic,
  );
  return withAlternatives({ family, style }, legacy);
}

type LayerFontRequest = {
  targetWeight: number;
  italic: boolean;
};

type FaceRank = {
  italicMismatch: number;
  weightDistance: number;
  variableRank: number;
};

function selectPreferredFace(
  faces: readonly IndexedFontFace[],
  request: LayerFontRequest,
): IndexedFontFace {
  return faces.reduce((best, face) =>
    compareFaceRank(rankFace(face, request), rankFace(best, request)) < 0
      ? face
      : best,
  );
}

function compareFaceRank(left: FaceRank, right: FaceRank): number {
  if (left.italicMismatch !== right.italicMismatch) {
    return left.italicMismatch - right.italicMismatch;
  }
  if (left.weightDistance !== right.weightDistance) {
    return left.weightDistance - right.weightDistance;
  }
  return left.variableRank - right.variableRank;
}

function rankFace(face: IndexedFontFace, request: LayerFontRequest): FaceRank {
  return {
    // Every mismatch shares one rank.
    // A catalog that only uploaded the other slope still resolves to that file.
    italicMismatch: (face.file.style === "italic") === request.italic ? 0 : 1,
    weightDistance: weightDistance(face, request.targetWeight),
    variableRank: isVariableFontFile(face.file) ? 1 : 0,
  };
}

function weightDistance(face: IndexedFontFace, targetWeight: number): number {
  const parts = (face.file.weight ?? "").split(" ");
  const min = Number(parts[0]);
  if (!Number.isFinite(min)) {
    return Number.MAX_SAFE_INTEGER;
  }
  const max = parts[1] ? Number(parts[1]) : min;
  if (targetWeight >= min && targetWeight <= max) {
    return 0;
  }
  return Math.min(Math.abs(targetWeight - min), Math.abs(targetWeight - max));
}

function withAlternatives(
  primary: FigmaFontCandidate,
  legacy: FigmaFontResolution,
): FigmaFontResolution {
  const seen = new Set<string>([key(primary.family, primary.style)]);
  const alternatives: FigmaFontCandidate[] = [];
  const push = (candidate: FigmaFontCandidate) => {
    const k = key(candidate.family, candidate.style);
    if (!seen.has(k)) {
      seen.add(k);
      alternatives.push(candidate);
    }
  };
  for (const candidate of spellingVariants(primary.family, primary.style)) {
    push(candidate);
  }
  push({ family: legacy.family, style: legacy.style });
  for (const candidate of legacy.alternatives) push(candidate);
  return { ...primary, alternatives };
}

const WEIGHT_TOKENS = [
  "thin",
  "hairline",
  "extralight",
  "ultralight",
  "light",
  "regular",
  "normal",
  "book",
  "roman",
  "medium",
  "semibold",
  "demibold",
  "demi",
  "bold",
  "extrabold",
  "ultrabold",
  "heavy",
  "black",
  "variable",
  "vf",
];

const NAMED_WEIGHT_REGEX = new RegExp(
  `^(.+?)[-_ ]?(${WEIGHT_TOKENS.join("|")})$`,
  "i",
);
const NUMERIC_WEIGHT_REGEX = /^(.+?)[-_ ]?(\d{2,4})$/;
// `italic` / `oblique` allow an optional separator (`-`, `_`, space). The
// Adobe-style 2-letter `it` abbreviation requires a separator so common
// trailing-`it` words (`Bit`, `Outfit`, …) don't false-positive as italic.
const ITALIC_REGEX = /^(.+?)(?:[-_ ]?(italic|oblique)|[-_ ](it))$/i;

/**
 * Parse a font filename (or CSS family string with the same convention) into
 * a clean family name and a weight/style label. Strips the extension, then
 * peels off a trailing italic/oblique token and a trailing weight token
 * (named or 3-digit numeric). Whatever remains becomes the family, then
 * gets CamelCase/dash prettified. The weight token is normalized via
 * `TOKEN_TO_STYLE_LABEL` so compound tokens (`ExtraBold`, `SemiBold`, …)
 * come out spaced (`"Extra Bold"`, `"Semi Bold"`), matching the most common
 * Figma style spelling. The Figma no-space variants (`"ExtraBold"`) are
 * emitted as fallbacks by `spellingVariants`.
 */
function parseFontFilename(filename: string): {
  /** Cleaned-up family name (e.g. "TWK Lausanne", "Helvetica Neue"). */
  family: string;
  /**
   * Display label for the weight + style combo (e.g. "Bold", "Bold Italic",
   * "400", "Regular"). Empty string when no weight/italic token was detected
   * and the file has no obvious style suffix.
   */
  weightLabel: string;
} {
  const stem = filename.replace(/\.[^.]+$/, "");

  let working = stem;
  let italic = false;
  // Capture group 1 is the family; either group 2 (`italic`/`oblique`) or
  // group 3 (`it`) is the matched italic token.
  const italicMatch = working.match(ITALIC_REGEX);
  if (italicMatch?.[1] && (italicMatch[2] || italicMatch[3])) {
    italic = true;
    working = italicMatch[1];
  }

  let weightToken: string | undefined;
  const namedMatch = working.match(NAMED_WEIGHT_REGEX);
  if (namedMatch?.[1] && namedMatch[2]) {
    weightToken = formatWeightToken(namedMatch[2]);
    working = namedMatch[1];
  } else {
    const numericMatch = working.match(NUMERIC_WEIGHT_REGEX);
    if (numericMatch?.[1] && numericMatch[2]) {
      weightToken = numericMatch[2];
      working = numericMatch[1];
    }
  }

  const familyRaw = working.replace(/[-_ ]+$/, "").trim() || stem;
  const family = prettifyFamilyName(familyRaw);

  let weightLabel = weightToken ?? "";
  if (italic) {
    weightLabel =
      weightLabel === "" || weightLabel === "Regular"
        ? "Italic"
        : `${weightLabel} Italic`;
  }

  return { family, weightLabel };
}

/**
 * Prettify a family stem: turn `TWKLausanne` into `TWK Lausanne`,
 * `helvetica-neue` into `Helvetica Neue`, leave `Inter` alone.
 *
 * Two CamelCase rules applied in order:
 *   1. Lowercase/digit followed by uppercase → insert space (`abC` → `ab C`)
 *   2. Run of uppercase followed by uppercase+lowercase → insert space before
 *      the last upper (`TWKLausanne` → `TWK Lausanne`, preserving the
 *      acronym).
 * Then collapses `-`/`_` to spaces, title-cases all-lowercase tokens, and
 * trims whitespace.
 */
function prettifyFamilyName(raw: string): string {
  if (!raw) return raw;
  const spaced = raw
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return spaced
    .split(" ")
    .map((word) => {
      // Leave words that already have an uppercase letter untouched —
      // preserves acronyms ("TWK", "SF") and already-correct case.
      if (!word || /[A-Z]/.test(word)) return word;
      return word[0]!.toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Pre-metadata fallback: resolve a CSS `font-family` value to the Figma
 * `(family, style)` pair that names the **file** the layer references. Both
 * the family and the style come from the filename. The layer's CSS
 * `fontWeight` / `italic` are deliberately not inputs here — they tell the
 * renderer what to compose with the file (potentially via faux bold /
 * faux italic), not which file to pick.
 */
function resolveFromFilename(cssFamily: string): FigmaFontResolution {
  const normalized = cssFamily.replace(/^['"]|['"]$/g, "").trim();
  if (!normalized) {
    return { family: cssFamily, style: "Regular", alternatives: [] };
  }

  const parsed = parseFontFilename(normalized);
  const family = parsed.family;
  // Style defaults to "Regular" for filenames without a recognized
  // weight/italic suffix. The plugin synthesizes any extra weight/italic
  // from the layer's own CSS on top of whatever face we load.
  const style = parsed.weightLabel || "Regular";
  return { family, style, alternatives: spellingVariants(family, style) };
}

// First-word style tokens that some fonts register as their own family name
// (e.g. "Arial Black", "Helvetica Narrow"). When the resolved style starts
// with one of these, we emit the compound-family form as a candidate so the
// plugin can try it before falling back to (family + weight-style).
const COMPOUND_FAMILY_TOKENS = new Set(["Black", "Narrow", "Condensed"]);

function spellingVariants(family: string, style: string): FigmaFontCandidate[] {
  const out: FigmaFontCandidate[] = [];
  const seen = new Set<string>([key(family, style)]);
  const push = (candidate: FigmaFontCandidate) => {
    const k = key(candidate.family, candidate.style);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(candidate);
    }
  };

  // Compound family: when the first style word is a known sub-family token
  // (Black, Narrow, …), try `{family + " " + token, remaining-style}` as a
  // fallback. e.g. ("Arial", "Black") → ("Arial Black", "Regular").
  // ("Arial", "Black Italic") → ("Arial Black", "Italic").
  const styleWords = style.split(/\s+/).filter(Boolean);
  if (styleWords.length > 0 && COMPOUND_FAMILY_TOKENS.has(styleWords[0]!)) {
    const compoundFamily = `${family} ${styleWords[0]}`;
    const remaining = styleWords.slice(1).join(" ") || "Regular";
    push({ family: compoundFamily, style: remaining });
  }

  // No-space style variant: "Extra Bold" → "ExtraBold". Some fonts (e.g.
  // Montserrat) register the no-space form.
  if (style.includes(" ")) {
    push({ family, style: style.replace(/\s+/g, "") });
  } else {
    const spaced = style.replace(/([a-z])([A-Z])/g, "$1 $2");
    if (spaced !== style) push({ family, style: spaced });
  }

  return out;
}

function key(family: string, style: string): string {
  return `${family}\x00${style}`;
}

// Canonical Figma style label for each known filename weight token. Compound
// tokens get spaced (`extrabold` → `"Extra Bold"`) — the most common Figma
// spelling. `spellingVariants` emits the no-space variant (`"ExtraBold"`) as
// a fallback for fonts registered the other way.
const TOKEN_TO_STYLE_LABEL: Record<string, string> = {
  thin: "Thin",
  hairline: "Hairline",
  extralight: "Extra Light",
  ultralight: "Ultra Light",
  light: "Light",
  regular: "Regular",
  normal: "Regular",
  book: "Book",
  roman: "Roman",
  medium: "Medium",
  semibold: "Semi Bold",
  demibold: "Demi Bold",
  demi: "Demi",
  bold: "Bold",
  extrabold: "Extra Bold",
  ultrabold: "Ultra Bold",
  heavy: "Heavy",
  black: "Black",
  variable: "Variable",
  vf: "Variable",
};

function formatWeightToken(token: string): string {
  const label = TOKEN_TO_STYLE_LABEL[token.toLowerCase()];
  if (label) return label;
  return token[0]!.toUpperCase() + token.slice(1).toLowerCase();
}
